import { Paper, Title, Grid } from '@mantine/core';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { LicenceRecord } from '@lms/shared';

interface ChartsProps {
  licences: LicenceRecord[];
}

export const Charts = ({ licences }: ChartsProps) => {
  // Status color mapping (matching table colors)
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return '#40c057'; // green
      case 'Inactive': return '#fa5252'; // red
      case 'Pending': return '#fd7e14'; // orange
      case 'Active (No Hub)': return '#fab005'; // yellow
      default: return '#868e96'; // gray
    }
  };

  // Process status data
  const statusData = licences.reduce((acc, licence) => {
    const status = licence.status || 'Unknown';
    const displayStatus = status === 'ACTIVE' ? 'Active' :
                         status === 'INACTIVE' ? 'Inactive' :
                         status === 'PENDING' ? 'Pending' :
                         status === 'ACTIVE_NO_HUB_ACCOUNT_ID' ? 'Active (No Hub)' :
                         status;
    
    acc[displayStatus] = (acc[displayStatus] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const statusChartData = Object.entries(statusData).map(([status, count]) => ({
    name: status,
    count,
    color: getStatusColor(status),
  }));

  // Process version data
  const versionData = licences.reduce((acc, licence) => {
    const version = licence.version || 'Not defined';
    acc[version] = (acc[version] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const versionChartData = Object.entries(versionData).map(([version, count]) => ({
    name: version,
    count,
  }));

  // Process licence type data for pie chart
  const licenceTypeData = licences.reduce((acc, licence) => {
    const type = licence.licence_type || 'Unknown';
    const displayType = type === 'MARKETPLACE' ? 'Marketplace' : 
                       type === 'PRIVATE' ? 'Private' : 
                       type;
    
    acc[displayType] = (acc[displayType] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const licenceTypePieData = Object.entries(licenceTypeData).map(([type, count]) => ({
    name: type,
    value: count,
    color: type === 'Marketplace' ? '#228be6' : '#fd7e14', // blue for Marketplace, orange for Private
  }));

  return (
    <Grid mb="xl">
      <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
        <Paper shadow="md" p="lg" radius="lg" withBorder style={{ backgroundColor: 'white' }}>
          <Title order={4} mb="md" c="#228be6" fw={600}>Licences by Status</Title>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={statusChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e7ff" />
              <XAxis dataKey="name" tick={{ fill: '#1e40af', fontSize: 11 }} />
              <YAxis tick={{ fill: '#1e40af', fontSize: 11 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255,255,255,0.95)', 
                  border: 'none', 
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }} 
              />
              <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                {statusChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Grid.Col>
      
      <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
        <Paper shadow="md" p="lg" radius="lg" withBorder style={{ backgroundColor: 'white' }}>
          <Title order={4} mb="md" c="#228be6" fw={600}>Licences by Version</Title>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={versionChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e7ff" />
              <XAxis dataKey="name" tick={{ fill: '#1e40af', fontSize: 11 }} />
              <YAxis tick={{ fill: '#1e40af', fontSize: 11 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255,255,255,0.95)', 
                  border: 'none', 
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }} 
              />
              <Bar dataKey="count" fill="#dc2626" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
        <Paper shadow="md" p="lg" radius="lg" withBorder style={{ backgroundColor: 'white' }}>
          <Title order={4} mb="md" c="#228be6" fw={600}>Licences by Type</Title>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={licenceTypePieData}
                cx="50%"
                cy="50%"
                outerRadius={70}
                dataKey="value"
                label={({ name, value }) => `${name}: ${value}`}
                labelLine={{ stroke: '#1e40af' }}
              >
                {licenceTypePieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} stroke="white" strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255,255,255,0.95)', 
                  border: 'none', 
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }} 
              />
            </PieChart>
          </ResponsiveContainer>
        </Paper>
      </Grid.Col>
    </Grid>
  );
};
